<template>
  <div>
    <div v-if="!loading">
      <h1 class="text-lg leading-tight pb-2 xl:text-2.5xl xl:pb-4">
        {{ $t('components.summary.title') }}
      </h1>

      <div v-if="errors.length" ref="error" class="w-full flex border border-gray-300 bg-white p-4 mt-2 mb-4">
        <div class="w-8 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" v-bind:svg-inline="''" v-bind:class="'w-4.5 h-4.5'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.6 0H5.269L0 5.4v7.2L5.4 18h7.2l5.4-5.4V5.4zm1.665 12.189l-2.121 2.121-3.189-3.19-3.189 3.19-2.122-2.121L6.837 9l-3.19-3.189 2.122-2.122 3.189 3.19 3.189-3.19 2.121 2.122L11.08 9z" fill="#c00"/></svg>
        </div>
        <div class="pt-2px xl:pt-0">
          <p class="font-bold text-sm leading-none pb-2 xl:text-lg">
            {{ $t(`errors.type.${firstErrorKey}.title`, brandInfo) }}
          </p>
          <p class="text-sm xl:text-sm" v-html="$t(`errors.type.${firstErrorKey}.description`, brandInfo)" />
        </div>
      </div>

      <template>
        <div class="w-full mb-4 bg-white border border-gray-300">
          <div class="px-3 py-2 font-bold leading-tight border-b border-gray-100 xl:text-lg">
            {{ $t('components.summary.table.vouchers.title') }}
          </div>
          <div
            v-for="appliedVoucher in redemption.appliedVouchers"
            :key="appliedVoucher.id"
            class="text-sm xl:text-lg"
          >
            <div class="flex justify-between px-3 py-2">
              <div class="mr-3 font-bold">
                {{ translateType(appliedVoucher.type) }}
                {{ $t('components.summary.table.vouchers.voucher_validity', { date: $d(new Date(appliedVoucher.validUntil)) }) }}
                - {{ appliedVoucher.code }}
              </div>
              <div class="flex items-center flex-shrink-0 font-bold font-numeric">
                {{ appliedVoucher.balanceInCents | lhFormatCurrency(currency, market) }}
                <button
                  class="flex-shrink-0 w-6 h-6 ml-2 transition-colors duration-300 ease-in-out text-navy-700 hover:text-navy-800"
                  :aria-label="$t('components.summary.flightVoucher.removeVoucher')"
                  @click.prevent="deleteVoucher(appliedVoucher)"
                >
                  <LibIconDelete :size="24" class="fill-current" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="shouldShowAddAnotherVoucher" class="mb-6">
          <div
            class="inline-flex items-center transition-colors duration-300 ease-in-out cursor-pointer text-navy-800 hover:text-primary"
            @click="showAddVoucherPopup = true"
          >
            <div class="flex-shrink-0 w-5 h-5 mr-2 cursor-pointer">
              <LibIconLinkPopup class="fill-current" :size="20" />
            </div>
            {{ $t('components.summary.link.add_more_voucher') }}
          </div>
        </div>

        <div class="w-full mb-4 bg-white border border-gray-300">
          <div class="px-3 py-2 font-bold leading-tight border-b border-gray-100 xl:text-lg">
            {{ $t('components.summary.table.ticket.title') }}
          </div>
          <div
            class="text-sm xl:text-lg"
          >
            <div class="flex justify-between px-3 py-2">
              <div class="mr-3 font-bold">
                {{ $t('components.summary.table.ticket.ticketPrice') }}
              </div>
              <div class="font-bold font-numeric">
                {{ redemption.amount | lhFormatCurrency(currency, market) }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <div class="w-full mb-4 bg-white border border-gray-300" :class="{ 'border-b-0': !paymentRequired }">
        <div class="px-3 py-2 font-bold leading-tight border-b border-gray-100 xl:text-lg">
          {{ $t('components.summary.finalSummary.title') }}
        </div>
        <div
          class="text-sm xl:text-lg"
          :class="[paymentRequired ? 'border-b-1' : 'border-b-2 border-secondary xl:border-b-3']"
        >
          <div class="flex justify-between px-3 py-2">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.flightVoucherBalance') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.vouchersBalance | lhFormatCurrency(currency, market) }}
            </div>
          </div>
          <div class="flex justify-between px-3 py-2">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.totalDue') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.totalDue | lhFormatCurrency(currency, market) }}
            </div>
          </div>
          <div class="flex justify-between px-3 py-2 font-bold">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.remainingFlightVoucherBalance') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ remainingFlightVoucherBalance | lhFormatCurrency(currency, market) }}
            </div>
          </div>
          <div class="flex justify-between px-3 py-2 font-bold">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.totalDue') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.fromCard | lhFormatCurrency(currency, market) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="paymentRequired" class="flex pt-6">
      <div class="relative w-full xl:max-w-xs">
        <button
          class="w-full px-2 py-3 text-base btn-secondary focus:outline-none"
          :class="{ 'disabled opacity-50': loading }"
          :disabled="loading"
          full-width
          @click="continueToCCPayment"
        >
          {{ $t('components.summary.continueToCreditCardButton') }}
        </button>
      </div>
    </div>

    <div v-else class="flex pt-6">
      <div class="relative w-full xl:max-w-xs">
        <button
          class="w-full px-2 py-3 text-base btn-secondary focus:outline-none"
          :class="{ 'disabled opacity-50': loading }"
          :disabled="loading"
          full-width
          @click="confirmRedemption"
        >
          {{ $t('components.summary.submitButton') }}
        </button>

        <div
          class="absolute top-0 left-0 flex-col items-center justify-center w-full h-full"
          :class="[loading ? 'flex' : 'hidden']"
        >
          <span class="text-sm text-center loading-text">{{ $t('general.loading') }}</span>
          <div id="lh-loading" class="relative flex-shrink-0 w-full h-3 mt-1 bullets-3">
            <div id="lh-loading-1" class="absolute top-0 w-3 h-3 lh-loading" />
            <div id="lh-loading-2" class="absolute w-3 h-3 lh-loading" />
            <div id="lh-loading-3" class="absolute w-3 h-3 lh-loading" />
          </div>
        </div>
      </div>
    </div>

    <RemoveVoucherPopup
      v-if="voucherToDelete"
      :value="true"
      :voucher="voucherToDelete"
      @input="onRemoveVoucherInput"
    />
    <AddVoucherPopup v-model="showAddVoucherPopup" />
    <CancelRedemptionPopup v-model="showExitPopup" />
  </div>
</template>

<script lang="ts">
import RemoveVoucherPopup from '~app/psp/lufthansa/remove-voucher-popup.vue'
import Summary from '~psp-pages/base/redemption/summary.ts'

import AddVoucherPopup from '~app/psp/lufthansa/add-voucher-popup.vue'
import CancelRedemptionPopup from '~app/psp/lufthansa/cancel-redemption-popup.vue'
import LibIconDelete from '~components/LibIcon/LibIconDelete.vue'
import LibIconLinkPopup from '~components/LibIcon/LibIconLinkPopup.vue'
import Payment from './payment.vue'

export default Summary.extend({
  components: {
    LibIconLinkPopup,
    LibIconDelete,
    RemoveVoucherPopup,
    Payment,
    AddVoucherPopup,
    CancelRedemptionPopup,
  },
})
</script>
